import React, { useEffect, useState } from "react";
import "./businessUsers.css";
import {
  Breadcrumb,
  Row,
  Col,
  Card,
  Button,
  Space,
  Input,
  Drawer,
  Descriptions,
  Tabs,
  Tag,
} from "antd";
import { Table } from "ant-table-extensions";
import http from "../../helpers/http";
import helper from "../../helpers/helper";
import config from "../../config/config";
import moment from "moment";
import { jwtDecode } from "jwt-decode";
import axios from "axios";
import { Modal } from "antd";

function BusinessUsersTrash() {
  const [dataSource, setDataSource] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [viewData, setViewData] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const base_url = config.api_url;
  const token = localStorage.getItem(config.jwt_store_key);
  const userRole = jwtDecode(token);

  /* ================= FETCH DATA ================= */
  const fetchTrashData = async () => {
    try {
      setLoading(true);

      const res = await axios.get(`${base_url}admin/business-users/trash`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // ✅ CORRECT PATH FOR YOUR API
      const list = res?.data?.data?.data || [];

      //   console.log("Trash List:", list); // 👈 MUST SHOW ARRAY

      setDataSource(list);
      setFilteredData(list);
    } catch (error) {
      console.error("Trash fetch error", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrashData();
  }, []);

  const handleBulkDelete = () => {
    Modal.confirm({
      title: "Are you sure?",
      content: `You are about to permanently delete ${selectedRowKeys.length} user.`,
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          setLoading(true);

          // ✅ CALL BULK BACKGROUND DELETE API
          await axios.post(
            `${base_url}admin/business-users/bulk-permanent-delete`,
            {
              users: selectedRowKeys,
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          // ✅ INSTANT UI UPDATE (Optimistic)
          const updatedData = dataSource.filter(
            (item) => !selectedRowKeys.includes(item._id)
          );

          setDataSource(updatedData);
          setFilteredData(updatedData);
          setSelectedRowKeys([]);

          Modal.success({
            title: "Success",
            content: "Selected users deleted successfully.",
          });
        } catch (error) {
          console.error("Bulk delete failed", error);

          Modal.error({
            title: "Error",
            content: "Failed to start bulk delete process.",
          });
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const confirmRestore = (id) => {
    Modal.confirm({
      title: "Restore User?",
      content: "Do you want to restore this user?",
      okText: "Yes",
      okType: "primary",
      cancelText: "No",
      onOk: () => handleRestore(id), // pass only ID
    });
  };

  const confirmPermanentDelete = (id) => {
    Modal.confirm({
      title: "Permanently Delete User?",
      content:
        "This action cannot be undone. All business data will be permanently removed.",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: () => handlePermanentDelete(id),
    });
  };

  const handlePermanentDelete = async (id) => {
    try {
      setLoading(true);

      await axios.delete(
        `${base_url}admin/business-users/permanent-delete/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Remove deleted user from UI
      const updatedData = dataSource.filter((item) => item._id !== id);
      setDataSource(updatedData);
      setFilteredData(updatedData);
      setSelectedRowKeys(selectedRowKeys.filter((key) => key !== id));

      Modal.success({
        title: "Deleted",
        content: "Business account permanently deleted.",
      });
    } catch (error) {
      console.error("Permanent delete failed", error);

      Modal.error({
        title: "Error",
        content: "Failed to delete business account.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (id) => {
    try {
      setLoading(true);

      await axios.put(
        `${base_url}admin/business-users/restore-user/${id}`,
        {}, // empty request body
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const updatedData = dataSource.filter((item) => item._id !== id);
      setDataSource(updatedData);
      setFilteredData(updatedData);
      setSelectedRowKeys(selectedRowKeys.filter((key) => key !== id));

      // ✅ Static success message
      Modal.success({
        title: "Success",
        content: "User Data Restore Successfully.",
      });
    } catch (error) {
      console.error("Restore failed", error);

      Modal.error({
        title: "Error",
        content: "Failed to restore user. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  /* ================= SEARCH ================= */
  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);
    setSelectedRowKeys([]); // ✅ clear selection on search

    if (!value) {
      setFilteredData(dataSource);
      return;
    }

    setFilteredData(
      dataSource.filter((item) =>
        [item.business_name, item.email, item.city, item.country, item.status]
          .join(" ")
          .toLowerCase()
          .includes(value)
      )
    );
  };

  /* ================= VIEW ================= */
  const handleView = async (id) => {
    setLoading(true);
    const { data } = await http.get(`${base_url}admin/business-users/${id}`);
    if (data) {
      setViewData(data);
      setDrawerOpen(true);
    }
    setLoading(false);
  };

  /* ================= TABLE COLUMNS ================= */
  const columns = [
    {
      title: "Business Name",
      dataIndex: "business_name",
      key: "business_name",
      sorter: (a, b) => a.business_name?.localeCompare(b.business_name),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (text, record) => {
        if (!["registered", "completed"].includes(text)) {
          return <Tag color="yellow">In Progress</Tag>;
        }

        return (
          <Tag color={text === "registered" ? "blue" : "green"}>
            {helper.convertToTitleCase(text)}
          </Tag>
        );
      },
    },
    {
      title: "Deleted At",
      dataIndex: "deletedAt",
      sorter: (a, b) => moment(a.deletedAt).unix() - moment(b.deletedAt).unix(),
      render: (text) => helper.ISTDate(text),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Space>
          <Button type="primary" onClick={() => handleView(record._id)}>
            View Detail
          </Button>
          <Button type="default" onClick={() => confirmRestore(record._id)}>
            Restore
          </Button>

          <Button danger onClick={() => confirmPermanentDelete(record._id)}>
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => {
      setSelectedRowKeys(keys);
    },
  };

  /* ================= JSX ================= */
  return (
    <div>
      <Breadcrumb
        style={{ margin: "16px 0" }}
        items={[
          { title: "Dashboard" },
          { title: "Business Flow" },
          { title: "Trash Users" },
        ]}
      />

      <Row>
        <Col span={24}>
          <Card
            title="Trash Users"
            variant="borderless"
            extra={
              selectedRowKeys.length > 0 && (
                <Button danger onClick={handleBulkDelete}>
                  Delete Selected ({selectedRowKeys.length})
                </Button>
              )
            }
          >
            <Space style={{ marginBottom: 16 }}>
              <Input
                placeholder="Search..."
                value={searchTerm}
                onChange={handleSearch}
                style={{ width: 400 }}
              />
            </Space>

            <Table
              rowKey="_id"
              columns={columns}
              dataSource={filteredData}
              loading={loading}
              rowSelection={rowSelection}
              pagination={{
                defaultPageSize: 10,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "50", "100"],
              }}
            />
          </Card>
        </Col>
      </Row>

      {/* ================= VIEW DRAWER ================= */}
      <Drawer
        title="User Details"
        placement="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={1000}
      >
        {viewData && (
          <Tabs defaultActiveKey="1">
            <Tabs.TabPane tab="User Details" key="1">
              <div>
                <h3 style={{ marginBottom: "16px" }}>User Input Data</h3>

                <Descriptions
                  bordered
                  column={{ xxl: 1, xl: 1, lg: 1, md: 1, sm: 1, xs: 1 }}
                  size="small"
                >
                  <Descriptions.Item label="Business Types">
                    {[
                      ...(viewData?.business_types
                        ?.map((it) => it.name)
                        .filter((name) => name !== "Others") || []),
                      viewData?.other_busiess_type
                        ? `Others (${viewData.other_busiess_type})`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(", ") || "N/A"}
                  </Descriptions.Item>

                  <Descriptions.Item label="Onboarded From">
                    {viewData?.onboarding_source
                      ? helper.convertToTitleCase(viewData.onboarding_source)
                      : "Web"}
                  </Descriptions.Item>

                  <Descriptions.Item label="Business Name">
                    {viewData.business_name || "N/A"}
                  </Descriptions.Item>

                  <Descriptions.Item label="Email">
                    {viewData.email || "N/A"}
                  </Descriptions.Item>

                  <Descriptions.Item label="Username">
                    {viewData.username || "N/A"}
                  </Descriptions.Item>

                  <Descriptions.Item label="Phone">
                    {viewData.phone
                      ? viewData.country_code + viewData.phone
                      : "N/A"}
                  </Descriptions.Item>

                  <Descriptions.Item label="Whatsapp No">
                    {viewData.whatsapp_no
                      ? viewData.whatsapp_country_code + viewData.whatsapp_no
                      : "N/A"}
                  </Descriptions.Item>

                  <Descriptions.Item label="City">
                    {viewData.city || "N/A"}
                  </Descriptions.Item>

                  <Descriptions.Item label="Country">
                    {viewData.country || "N/A"}
                  </Descriptions.Item>

                  <Descriptions.Item label="Pincode">
                    {viewData.pincode || "N/A"}
                  </Descriptions.Item>

                  <Descriptions.Item label="Status">
                    {helper.convertToTitleCase(viewData.status) || "N/A"}
                  </Descriptions.Item>

                  <Descriptions.Item label="Profile Status">
                    {helper.convertToTitleCase(viewData.pageStatus) || "N/A"}
                  </Descriptions.Item>

                  <Descriptions.Item label="Verification Status">
                    {helper.convertToTitleCase(
                      viewData?.verificationData?.status
                    ) || "Not Applied"}
                  </Descriptions.Item>

                  <Descriptions.Item label="Created At">
                    {helper.ISTDate(viewData.createdAt)}
                  </Descriptions.Item>
                </Descriptions>
              </div>
            </Tabs.TabPane>
          </Tabs>
        )}
      </Drawer>
    </div>
  );
}

export default BusinessUsersTrash;
